import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Waves, Copy, CheckCircle2, Clock, XCircle, CalendarClock, Server, Loader2,
  AlertTriangle, Database, Bell, PlugZap, ArrowRight, Check, Lock, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Step2Config from "@/components/Step2Config";
import Step3Source from "@/components/Step3Source";
import Step4Categories from "@/components/Step4Categories";
import Step5Sync from "@/components/Step5Sync";
import { useT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYSTEMS = [
  { id: "xtream-ui", name: "XTREAM UI" },
  { id: "streamcreed", name: "STREAMCREED" },
  { id: "xui-one", name: "XUI ONE" },
  { id: "1-stream", name: "1-STREAM" },
];

const STATUS_MAP = {
  ativo: { label: "Ativo", cls: "bg-green-100 text-green-700 border-green-300", Icon: CheckCircle2 },
  suspenso: { label: "Suspenso", cls: "bg-yellow-100 text-yellow-800 border-yellow-300", Icon: Clock },
  pendente: { label: "Aguardando pagamento", cls: "bg-slate-100 text-slate-600 border-slate-300", Icon: Clock },
};

export default function Panel() {
  const { code, step: stepParam } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const step = Math.min(5, Math.max(1, parseInt(stepParam || "1", 10) || 1));
  const goStep = (n) => navigate(`/panel/${code}/${n}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [systemType, setSystemType] = useState("xui-one");
  const [mainIp, setMainIp] = useState("");
  const [dbPort, setDbPort] = useState("");
  const [dbName, setDbName] = useState("");
  const [dbUser, setDbUser] = useState("");
  const [dbPass, setDbPass] = useState("");
  const [telegram, setTelegram] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/panel/${code}`)
      .then((r) => {
        const d = r.data;
        setData(d);
        setSystemType(d.system_type || "xui-one");
        setTelegram(d.telegram || "");
        if (d.server_config) {
          setMainIp(d.server_config.main_ip || "");
          setDbPort(d.server_config.db_port || "3306");
          setDbName(d.server_config.db_name || "xui");
          setDbUser(d.server_config.db_user || "");
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [code]);

  const copyText = (txt, msg) => { navigator.clipboard.writeText(txt); toast.success(msg); };

  const manualCommands = data ?
`mysql -e "CREATE USER '${data.suggested_db_user}'@'%' IDENTIFIED BY '${data.suggested_db_pass}';"
mysql -e "GRANT ALL PRIVILEGES ON *.* TO '${data.suggested_db_user}'@'%';"
mysql -e "FLUSH PRIVILEGES;"` : "";

  const useSuggested = () => {
    copyText(manualCommands, t("step1.cmdsCopied"));
    setDbUser(data.suggested_db_user);
    setDbPass(data.suggested_db_pass);
  };

  const testTelegram = async () => {
    try {
      const { data: res } = await axios.post(`${API}/panel/${code}/telegram/test`);
      res.ok ? toast.success(res.message) : toast.error(res.message);
    } catch (_) { toast.error("Erro ao enviar teste do Telegram."); }
  };

  const testConnection = async () => {
    if (!mainIp || !dbUser) { toast.error(t("step1.fillIpUser")); return; }
    setTesting(true);
    try {
      const { data: res } = await axios.post(`${API}/panel/${code}/test-connection`, {
        main_ip: mainIp, db_port: dbPort || "3306", db_name: dbName || "xui", db_user: dbUser, db_pass: dbPass,
      });
      res.ok ? toast.success(res.message) : toast.error(res.message);
    } catch (e) {
      toast.error("Erro ao testar conexão.");
    } finally { setTesting(false); }
  };

  const advance = async () => {
    if (!mainIp || !dbUser) { toast.error(t("step1.fillIpUser")); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/panel/${code}/server`, {
        system_type: systemType, main_ip: mainIp, db_port: dbPort, db_name: dbName,
        db_user: dbUser, db_pass: dbPass, telegram,
      });
      toast.success(t("step1.savedStep1"));
      setData((d) => ({ ...d, server_config: { main_ip: mainIp, db_port: dbPort || "3306", db_name: dbName || "xui", db_user: dbUser } }));
      goStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar. Verifique os dados.");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-green-600" /></div>;
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-4 text-center">
        <XCircle className="h-12 w-12 text-red-500" />
        <h1 className="font-display text-2xl font-bold text-slate-800">{t("panel.notFoundTitle")}</h1>
        <p className="text-slate-500">{t("panel.notFoundDesc")}</p>
        <Link to="/"><Button className="bg-green-600 text-white hover:bg-green-700">{t("panel.backToPlans")}</Button></Link>
      </div>
    );
  }

  const st = STATUS_MAP[data.status] || STATUS_MAP.pendente;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("pt-BR") : "—";
  const isActive = data.status === "ativo";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center gap-2 px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-600 text-white">
              <Waves className="h-5 w-5" /></span>
            <span className="font-display text-xl font-extrabold text-green-700">WX SyncVods</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6" data-testid="panel-root">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{t("panel.clientPanel")}</p>
              <h1 className="mt-1 font-display text-2xl font-bold text-slate-900">{data.plan_title}</h1>
            </div>
            <span data-testid="panel-status-badge"
              className={`inline-flex items-center gap-1.5 self-start rounded-full border px-3 py-1 text-sm font-semibold ${st.cls}`}>
              <st.Icon className="h-4 w-4" /> {data.status === "ativo" ? t("panel.statusActive") : data.status === "suspenso" ? t("panel.statusSuspended") : t("panel.statusPending")}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Metric Icon={CalendarClock} label={t("panel.daysRemaining")} value={isActive ? `${data.days_remaining}` : "—"} testid="metric-days" />
            <Metric Icon={CheckCircle2} label={t("panel.expiresAt")} value={fmtDate(data.expires_at)} testid="metric-expires" />
            <Metric Icon={Server} label={t("panel.system")} value={SYSTEMS.find((s) => s.id === systemType)?.name || "XUI ONE"} testid="metric-system" />
          </div>
        </motion.div>

        {isActive && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="step-nav">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`step-nav-${n}`} onClick={() => goStep(n)}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-all ${step === n ? "bg-green-600 text-white shadow-sm" : "border border-slate-200 bg-white text-slate-600 hover:border-green-300"}`}>
                {t("panel.stepLabel")} {n}
              </button>
            ))}
          </div>
        )}

        {!isActive ? (
          data.category === "trial" ? (
            <div className="mt-6 rounded-xl border border-green-300 bg-gradient-to-br from-green-50 to-emerald-50 p-6 text-center" data-testid="panel-trial-expired">
              <h2 className="font-display text-xl font-bold text-slate-800">Seu teste de 1 dia terminou 🎬</h2>
              <p className="mx-auto mt-1 max-w-md text-sm text-slate-600">Assine para liberar o painel na hora. Suas configurações e o IP do servidor ficam salvos — você continua exatamente de onde parou.</p>
              <div className="mt-4">
                <Link to={`/?renew=${code}`}>
                  <Button className="bg-green-600 px-6 py-2.5 font-bold text-white hover:bg-green-700" data-testid="panel-subscribe-btn">
                    Assinar agora para liberar
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-xl border border-yellow-300 bg-yellow-50 p-6 text-sm text-yellow-800" data-testid="panel-inactive">
              {data.status === "suspenso"
                ? t("panel.inactiveSuspended")
                : t("panel.inactivePending")}
              <div className="mt-4"><Link to={`/checkout/${data.plan_id}?renew=${code}`}><Button className="bg-green-600 text-white hover:bg-green-700" data-testid="panel-renew-btn">
                {t("panel.renewBtn")}</Button></Link></div>
            </div>
          )
        ) : step === 1 ? (
          <div className="mt-6 space-y-6" data-testid="panel-step1">
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t("step1.attention")}</span>
            </div>

            {/* System type */}
            <section>
              <p className="mb-3 text-slate-700">{t("step1.selectSystem")}</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {SYSTEMS.map((s) => (
                  <button key={s.id} data-testid={`system-${s.id}`} onClick={() => setSystemType(s.id)}
                    className={`rounded-xl border-2 px-3 py-5 text-center font-display text-sm font-bold transition-all ${
                      systemType === s.id
                        ? "border-green-500 bg-green-50 text-green-700 shadow-sm"
                        : "border-slate-200 bg-white text-slate-500 hover:border-green-300"
                    }`}>
                    {s.name}
                    {systemType === s.id && <Check className="mx-auto mt-1 h-4 w-4 text-green-600" />}
                  </button>
                ))}
              </div>
            </section>

            <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
              {t("step1.oneServerNote")}
            </div>

            {/* Dados do Servidor */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-800">
                <Database className="h-5 w-5 text-green-600" /> {t("step1.serverData")}
              </h2>

              <div className="mt-4 rounded-lg border border-yellow-300 bg-yellow-50 p-4">
                <p className="text-sm font-semibold text-yellow-900">{t("step1.manualTitle")}</p>
                <p className="mt-1 text-xs text-yellow-800">
                  {t("step1.manualDesc")}
                </p>
                <pre data-testid="manual-commands" className="font-mono-code mt-3 overflow-x-auto rounded-md bg-slate-900 p-3 text-[11px] leading-relaxed text-green-300">
{manualCommands}
                </pre>
                <Button size="sm" data-testid="use-suggested-btn" onClick={useSuggested}
                  className="mt-3 bg-green-600 text-white hover:bg-green-700">
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> {t("step1.copyUseSuggested")}
                </Button>
              </div>

              <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-4">
                <p className="text-sm font-semibold text-sky-900">{t("step1.autoTitle")}</p>
                <p className="mt-1 text-xs text-sky-800">
                  {t("step1.autoDesc")}
                </p>
                <pre data-testid="install-command" className="font-mono-code mt-3 overflow-x-auto rounded-md bg-slate-900 p-3 text-[11px] text-sky-200">
{data.install_command}
                </pre>
                <Button size="sm" variant="outline" data-testid="copy-install-btn"
                  onClick={() => copyText(data.install_command, t("step1.commandCopied"))} className="mt-3">
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> {t("step1.copyCommand")}
                </Button>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={t("step1.ipLabel")} testid="field-main-ip" value={mainIp} onChange={setMainIp}
                  placeholder={t("step1.ipPlaceholder")} disabled={!!data?.ip_locked}
                  hint={data?.ip_locked ? t("step1.ipLocked") : ""} />
                <Field label={t("step1.portLabel")} testid="field-db-port" value={dbPort} onChange={setDbPort} placeholder="3306" />
                <Field label={t("step1.dbNameLabel")} testid="field-db-name" value={dbName} onChange={setDbName} placeholder="xui" />
                <Field label={t("step1.dbUserLabel")} testid="field-db-user" value={dbUser} onChange={setDbUser} placeholder={t("step1.dbUserPlaceholder")} mono />
                <div className="sm:col-span-2">
                  <Field label={t("step1.dbPassLabel")} testid="field-db-pass" value={dbPass} onChange={setDbPass}
                    placeholder={t("step1.dbPassPlaceholder")} type="password" mono />
                </div>
              </div>
            </section>

            {/* Notificação */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-800">
                <Bell className="h-5 w-5 text-green-600" /> {t("step1.notifData")}
              </h2>

              <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 p-4">
                <p className="text-sm font-semibold text-sky-800">{t("step1.botAccess")}</p>
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-sky-900">
                  <li>{t("step1.botS1")}</li>
                  <li>{t("step1.botS2")}</li>
                  <li>{t("step1.botS3")}</li>
                  <li>{t("step1.botS4")}</li>
                </ol>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <a href="https://t.me/WXSyncVods_bot" target="_blank" rel="noopener noreferrer"
                     data-testid="telegram-bot-link"
                     className="inline-flex items-center gap-2 rounded-full bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-700">
                    <Send className="h-4 w-4" /> {t("step1.openBot")}
                  </a>
                  <a href={`https://t.me/WXSyncVods_bot?start=${code}`} target="_blank" rel="noopener noreferrer"
                     data-testid="telegram-connect-btn"
                     className="inline-flex items-center gap-2 rounded-full bg-green-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700">
                    <Send className="h-4 w-4" /> Conectar Telegram
                  </a>
                  {data?.telegram ? (
                    <button type="button" onClick={testTelegram} data-testid="telegram-test-btn"
                      className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-white px-4 py-2 text-sm font-semibold text-sky-700 transition-colors hover:bg-sky-100">
                      <Bell className="h-4 w-4" /> {t("step1.sendTest")}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="mt-3">
                <Field label={t("step1.chatIdLabel")} testid="field-telegram" value={telegram} onChange={setTelegram}
                  placeholder="Ex: 5914704792"
                  hint={data?.telegram ? t("step1.chatIdRegistered") : ""} />
              </div>
            </section>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" data-testid="test-connection-btn" onClick={testConnection} disabled={testing}>
                {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}
                {t("step1.testConn")}
              </Button>
              <Button data-testid="advance-btn" onClick={advance} disabled={saving}
                className="bg-green-600 py-6 text-base font-semibold text-white hover:bg-green-700 sm:py-2">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("common.advance")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : step === 2 ? (
          <Step2Config code={code} data={data} onBack={() => goStep(1)} onNext={async () => { try { const r = await axios.get(`${API}/panel/${code}`); setData(r.data); } catch (_) {} goStep(3); }} />
        ) : step === 3 ? (
          <Step3Source code={code} data={data} onBack={() => goStep(2)} onNext={async () => { try { const r = await axios.get(`${API}/panel/${code}`); setData(r.data); } catch (_) {} goStep(4); }} />
        ) : step === 4 ? (
          <Step4Categories code={code} data={data} onBack={() => goStep(3)} onNext={async () => { try { const r = await axios.get(`${API}/panel/${code}`); setData(r.data); } catch (_) {} goStep(5); }} />
        ) : (
          <Step5Sync code={code} data={data} goStep={goStep} />
        )}

        <div className="mt-8 flex flex-col items-center gap-2 border-t border-slate-200 py-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{t("panel.syncId")}</p>
          <div className="flex items-center gap-2">
            <code data-testid="panel-code" className="font-mono-code break-all text-sm text-slate-600">{data.sync_id}</code>
            <Button size="sm" variant="ghost" data-testid="panel-copy-code" onClick={() => copyText(data.sync_id, t("panel.codeCopied"))}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <span className="text-xs text-slate-400">{t("panel.copyHint")}</span>
        </div>
      </main>
    </div>
  );
}

const Metric = ({ Icon, label, value, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-400">
      <Icon className="h-4 w-4" />
      <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
    </div>
    <p className="mt-2 break-all font-display text-base font-bold text-slate-800">{value}</p>
  </div>
);

const Field = ({ label, value, onChange, placeholder, testid, type = "text", mono = false, disabled = false, hint = "" }) => (
  <div className="flex flex-col gap-1.5">
    <Label>{label}</Label>
    <Input data-testid={testid} type={type} value={value} placeholder={placeholder} disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={`${mono ? "font-mono-code text-xs" : ""}${disabled ? " cursor-not-allowed bg-slate-100 text-slate-500" : ""}`} />
    {hint ? <span className="flex items-center gap-1 text-[11px] text-amber-600" data-testid={`${testid}-hint`}><Lock className="h-3 w-3" />{hint}</span> : null}
  </div>
);
